/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import {
  intColorToHexColor,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
} from '@gh/common-shared';
import { DirtyType, EMPTY_ID, ID } from '@gh/common-shared-data';
import { S3UploadItem, UploadItem } from '@gh/core-ui';
import { isNotNil, maybeMap } from '@gh/core-util';
import {
  flatten,
  fromPairs,
  initial,
  intersection,
  isEmpty,
  isNil,
  keyBy,
  keys,
  last,
  map,
  memoize,
  omit,
  pick,
  uniq,
  uniqueId,
} from 'lodash';
import { Color, Point } from 'paper';
import { hexToRgbaColor } from '../../components/canvas/canvas.utils';
import {
  getGraphicalTakeOffHousePlanViewId,
  GraphicalTakeOffHousePlanSave,
  GraphicalTakeOffHousePlanView,
  graphicalTakeOffHousePlanViewArr,
} from './house-plan-view.model';
import {
  diffPoints,
  GraphicalTakeOffTop,
  GraphicalTakeOffTopCurrentSummary,
  graphicalTakeOffTopDataBoxToDto,
  GraphicalTakeOffTopPersistedSummary,
  GraphicalTakeOffTopView,
  newSize, pointLength,
} from './top.model';

export type GraphicalTakeOffSize = {
  width: number;
  height: number;
};

export enum GraphicalTakeOffChangeOption {
  CURRENT_SELECTION,
  CURRENT_VIEW,
  ALL_VIEWS,
}

export enum GtoShortcutScope {
  Global = 'global',
  Canvas = 'canvas',
  Editor = 'editor',
}

export enum GtoToolName {
  SetScale = 'SetScale',
  Crop = 'Crop',
}

export enum GraphicalTakeOffScaleDirection {
  Vertical = 'Vertical',
  Horizontal = 'Horizontal',
}

export type GraphicalTakeOffDetails = {
  tops: GraphicalTakeOffTop[];
  topViews: Hash<GraphicalTakeOffTopView[]>;
  views: GraphicalTakeOffHousePlanView[];
  viewTransform: GraphicalTakeOffViewTransform;
  zoom: number;
  selectedTop?: ID;
  selectedMeasurement?: ID;
  drawingTop?: ID;
  drawingMeasurement?: ID;
  highlightedTop?: ID;
  highlightedMeasurement?: ID;
  highlightedDataBox?: ID;
  currentView: ID;
  selectedView: ID;
  currentTop?: ID;
  isOverlay: boolean;
  isSnapToPoint: boolean;
  dataBoxVisibility: GraphicalTakeOffDataBoxVisibility;
  currentSummary: Hash<GraphicalTakeOffTopCurrentSummary>;
  persistedSummary: Hash<GraphicalTakeOffTopPersistedSummary>;
  loadedViewIndex: Hash<boolean>;
  loadedViewCount: number;
  priceBookId?: ID;
  activeToolName?: GtoToolName;
  measurementInProgress: boolean;
  isScaleInfoAccepted: boolean;
  history: GraphicalTakeOffHistory;
  tools: {
    scaling: {
      point1?: GraphicalTakeOffPoint;
      point2?: GraphicalTakeOffPoint;
      currentDirection?: GraphicalTakeOffScaleDirection;
      scaleWidth?: number;
      scaleHeight?: number;
      previousScaleWidth?: number;
      previousScaleHeight?: number;
      inProgress: boolean;
      isWaitingForLength: boolean;
    };
  };
};

export const GTO_IMAGE_PADDING_RATIO = 0.05;
export const GTO_VIEW_HISTORY_MAX_RECORD_COUNT = 40;

export const GTO_VIEW_HISTORY_EMPTY: GraphicalTakeOffViewHistory = { currentIndex: -1, records: [] };

export type GraphicalTakeOffViewHistory = {
  currentIndex: number;
  records: GraphicalTakeOffViewHistoryRecord[];
};

export type GraphicalTakeOffHistory = Hash<GraphicalTakeOffViewHistory>;

export type GraphicalTakeOffViewHistoryRecord = {
  recordId: ID;
  affectedViewIds: ID[];
  topViews: GraphicalTakeOffTopView[];
  view: GraphicalTakeOffHousePlanView;
  selectedTop?: ID;
  selectedMeasurement?: ID;
  drawingTop?: ID;
  drawingMeasurement?: ID;
  highlightedTop?: ID;
  highlightedMeasurement?: ID;
  highlightedDataBox?: ID;
  currentTop?: ID;
  activeToolName?: GtoToolName;
  measurementInProgress: boolean;
};

export enum GraphicalTakeOffDataBoxVisibility {
  ShowNoDataboxes = 'showNoDataboxes',
  ShowSelection = 'showSelection',
  ShowTopOnly = 'showTopOnly',
  ShowAll = 'showAll',
}

export const getGtoShortcutParentScope = (scope: GtoShortcutScope) => {
  switch (scope) {
    case GtoShortcutScope.Global:
      return void 0;
    case GtoShortcutScope.Editor:
      return GtoShortcutScope.Global;
    case GtoShortcutScope.Canvas:
      return GtoShortcutScope.Editor;
    default:
      return void 0;
  }
};

export const ZOOM_VALUES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.5, 4, 5, 6];

export const GTO_SCALING_TOOL_INIT = { inProgress: false, isWaitingForLength: false };
export const GTO_TOOLS_INIT = { scaling: GTO_SCALING_TOOL_INIT };

export const GRAPHICAL_TAKE_OFF_DETAILS_INIT: GraphicalTakeOffDetails = {
  viewTransform: {
    position: { x: 0, y: 0 },
    scale: 1,
  },
  zoom: 0,
  tops: [],
  topViews: {},
  views: [],
  currentView: EMPTY_ID,
  selectedView: EMPTY_ID,
  isOverlay: true,
  isSnapToPoint: true,
  dataBoxVisibility: GraphicalTakeOffDataBoxVisibility.ShowAll,
  currentSummary: {},
  persistedSummary: {},
  loadedViewIndex: {},
  loadedViewCount: 0,
  measurementInProgress: false,
  isScaleInfoAccepted: false,
  history: {},
  tools: {
    scaling: GTO_SCALING_TOOL_INIT,
  },
};

export type GraphicalTakeOffColorPalette = {
  primary: string;
  background: Color;
};

export type GraphicalTakeOffViewTransform = {
  position: GraphicalTakeOffPoint;
  scale: number;
};

export enum GraphicalTakeOffImageTransformType { Crop, Rotate }

export type GraphicalTakeOffImageCropTransform = {
  type: GraphicalTakeOffImageTransformType.Crop;
  lower: GraphicalTakeOffPoint;
  upper: GraphicalTakeOffPoint;
};
export type GraphicalTakeOffImageRotateTransform = {
  type: GraphicalTakeOffImageTransformType.Rotate;
  angle: number;
};
export type GraphicalTakeOffImageTransform = GraphicalTakeOffImageCropTransform | GraphicalTakeOffImageRotateTransform;
export type GraphicalTakeOffImageTransformPipeline = {
  originalSize: GraphicalTakeOffSize;
  size: GraphicalTakeOffSize;
  transformations: GraphicalTakeOffImageTransform[];
};

export const getGraphicalTakeOffColorPalette = memoize((code: number): GraphicalTakeOffColorPalette => {
  const primaryColor = intColorToHexColor(code);
  const backgroundColor = hexToRgbaColor(primaryColor);

  return {
    primary: primaryColor,
    background: backgroundColor,
  };
});

export const graphicalTakeOffCurrentView = (details: GraphicalTakeOffDetails) =>
  graphicalTakeOffHousePlanViewArr.find(details.views, details.currentView)!;

export const graphicalTakeOffDetailsToHousePlanSave = (details: GraphicalTakeOffDetails,
                                                       view: GraphicalTakeOffHousePlanView): GraphicalTakeOffHousePlanSave => ({
  viewId: getGraphicalTakeOffHousePlanViewId(view),
  priceBookId: details.priceBookId,
  pictureDetails: pick(view.picture, 'key', 'scaleWidth', 'scaleHeight'),
  tops: details.topViews[getGraphicalTakeOffHousePlanViewId(view)]
    // get rid of new top views if they were deleted
    .filter((topView) => !(isNil(topView.id) && isEmpty(topView.metadata.measures)))
    .map((topView) => ({
      id: topView.id,
      takeOffParam: {
        takeOffParamId: topView.takeOffParam.takeOffParamId,
      },
      jobViewId: topView.jobViewId,
      hpViewId: topView.hpViewId,
      metadata: {
        version: topView.metadata.version,
        isChanged: topView.metadata.isChanged,
        type: topView.metadata.type,
        measures: topView.metadata.measures.map((measurement) => ({
          ...omit(measurement, 'id', 'drawingMode', 'draftMode'),
          mainDatabox: maybeMap(measurement.mainDatabox, graphicalTakeOffTopDataBoxToDto),
          databoxes: map(measurement.databoxes, graphicalTakeOffTopDataBoxToDto),
        })),
      },
      dirtyType: isNil(topView.id)
        ? DirtyType.New
        : (isEmpty(topView.metadata.measures) ? DirtyType.Removed : DirtyType.Updated),
    })),
  version: view.version,
});

export const graphicalTakeOffDetailsToHousePlanSaveWithImage = (details: GraphicalTakeOffDetails,
                                                                view: GraphicalTakeOffHousePlanView,
                                                                item?: UploadItem): GraphicalTakeOffHousePlanSave => {
  const saveModel = graphicalTakeOffDetailsToHousePlanSave(details, view);
  if (item) {
    return {
      ...saveModel,
      pictureDetails: {
        ...saveModel.pictureDetails,
        key: (<S3UploadItem>item).key!,
      },
    };
  } else {
    return saveModel;
  }
};

export const graphicalTakeOffImageTransform = (image: GraphicalTakeOffImageTransformPipeline,
                                               transform: GraphicalTakeOffImageTransform): GraphicalTakeOffImageTransformPipeline => ({
  originalSize: image.originalSize,
  transformations: image.transformations.concat([transform]),
  size: graphicalTakeOffSizeTransform(image.size, transform),
});

export const newGraphicalTakeOffImageCropTransformation = (lower: GraphicalTakeOffPoint,
                                                           upper: GraphicalTakeOffPoint): GraphicalTakeOffImageCropTransform => ({
  type: GraphicalTakeOffImageTransformType.Crop,
  lower,
  upper,
});

export const newGraphicalTakeOffImageRotateTransformation = (angle: number): GraphicalTakeOffImageRotateTransform => ({
  type: GraphicalTakeOffImageTransformType.Rotate,
  angle,
});

const graphicalTakeOffSizeTransform = (size: GraphicalTakeOffSize,
                                       transform: GraphicalTakeOffImageTransform): GraphicalTakeOffSize => {
  switch (transform.type) {
    case GraphicalTakeOffImageTransformType.Crop:
      return newSize(transform.upper.x - transform.lower.x, transform.upper.y - transform.lower.y);
    case GraphicalTakeOffImageTransformType.Rotate: {
      const point = new Point(size.width, size.height).rotate(transform.angle);
      return newSize(point.x, point.y);
    }
    default:
      return size;
  }
};

export const graphicalTakeOffImageAppendTransform = (pipeline: GraphicalTakeOffImageTransformPipeline,
                                                     transformation: GraphicalTakeOffImageTransform) => {
  const lastTransformation = last(pipeline.transformations);

  if (isNil(lastTransformation) || lastTransformation.type !== transformation.type) {
    return graphicalTakeOffImageSetTransformation(pipeline, transformation);
  } else if (transformation.type === GraphicalTakeOffImageTransformType.Crop) {
    return graphicalTakeOffImageTransform(pipeline, transformation);
  } else if (transformation.type === GraphicalTakeOffImageTransformType.Rotate) {
    return graphicalTakeOffImageSetTransformation(
      pipeline,
      newGraphicalTakeOffImageRotateTransformation(
        normalizeAngle((<GraphicalTakeOffImageRotateTransform>lastTransformation).angle + transformation.angle)));
  } else {
    return pipeline;
  }
};

export const graphicalTakeOffImageSetTransformation = (pipeline: GraphicalTakeOffImageTransformPipeline,
                                                       transformation: GraphicalTakeOffImageTransform) => {
  const lastTransformation = last(pipeline.transformations);

  if (isNil(lastTransformation) || lastTransformation.type !== transformation.type) {
    if (isNullImageTransformation(pipeline, transformation)) {
      return pipeline;
    } else {
      return graphicalTakeOffImageTransform(pipeline, transformation);
    }
  } else {
    if (isNullImageTransformation(pipeline, transformation)) {
      return {
        ...pipeline,
        transformations: initial(pipeline.transformations),
      };
    } else {
      return {
        ...pipeline,
        transformations: initial(pipeline.transformations).concat([transformation]),
      };
    }
  }
};

export const graphicalTakeOffImagePipeline = (image: HTMLImageElement) => {
  const size = newSize(image.width, image.height);

  return {
    originalSize: size,
    size,
    transformations: [],
  };
};

export const getGraphicalTakeOffImageLastRotation = (pipeline: GraphicalTakeOffImageTransformPipeline) => {
  const lastTransformation = last(pipeline.transformations);

  if (isNotNil(lastTransformation) && lastTransformation.type === GraphicalTakeOffImageTransformType.Rotate) {
    return lastTransformation.angle;
  } else {
    return 0;
  }
};

const normalizeAngle = (angle: number) => {
  if (angle < -180) {
    return 360 + angle;
  } else if (angle > 180) {
    return angle - 360;
  } else {
    return angle;
  }
};

const isNullImageTransformation = (pipeline: GraphicalTakeOffImageTransformPipeline,
                                   transform: GraphicalTakeOffImageTransform) => {
  switch (transform.type) {
    case GraphicalTakeOffImageTransformType.Rotate:
      return transform.angle === 0;
    case GraphicalTakeOffImageTransformType.Crop:
      return transform.lower.x === 0 && transform.lower.y === 0
        && transform.upper.x === pipeline.size.width && transform.upper.y === pipeline.size.height;
    default:
      return false;
  }
};

export const compressGtoViewHistory = (history: GraphicalTakeOffViewHistory) => {
  if (history.currentIndex > GTO_VIEW_HISTORY_MAX_RECORD_COUNT) {
    const countOfRemovedRecords = history.currentIndex - GTO_VIEW_HISTORY_MAX_RECORD_COUNT;
    return setEntityFields(history, {
      currentIndex: history.currentIndex - countOfRemovedRecords,
      records: history.records.slice(countOfRemovedRecords),
    });
  } else {
    return history;
  }
};

export const pushGtoViewHistoryRecords = (details: GraphicalTakeOffDetails, affectedViewIds: ID[]) => {
  // take next record in all affected views and clean them all from all views they belong to
  const affectedViewIdsForNextRecords = flatten(affectedViewIds
    .map((viewId) => getGtoViewHistoryRecord(details, viewId, 1))
    .filter(Boolean)
    .map((record: GraphicalTakeOffViewHistoryRecord) => record.affectedViewIds));

  const truncatedHistory = setEntityFields(details.history, fromPairs(affectedViewIdsForNextRecords.map((viewId) => [
    viewId,
    truncateGtoViewFutureHistory(details.history[viewId]),
  ])));

  // push new history record for all affected views
  const recordId = generateGtoViewHistoryRecordId();
  return setEntityField(
    details,
    'history',
    setEntityFields(truncatedHistory, fromPairs(affectedViewIds.map((viewId) => [
      viewId,
      addGtoViewHistoryRecord(
        truncatedHistory[viewId],
        makeGtoViewHistoryRecord(details, viewId, affectedViewIds, recordId)),
    ]))));
};

const addGtoViewHistoryRecord = (history: GraphicalTakeOffViewHistory,
                                 record: GraphicalTakeOffViewHistoryRecord) => {
  return compressGtoViewHistory(setEntityFields(history, {
    currentIndex: history.currentIndex + 1,
    records: history.records.slice(0, history.currentIndex + 1).concat([record]),
  }));
};

export const truncateGtoViewFutureHistory = (history: GraphicalTakeOffViewHistory) =>
  history.currentIndex + 1 < history.records.length
    ? setEntityField(history, 'records', history.records.slice(0, history.currentIndex + 1))
    : history;

export const generateGtoViewHistoryRecordId = () => uniqueId('gto:');

export const makeGtoViewHistoryRecord = (details: GraphicalTakeOffDetails,
                                         viewId: ID,
                                         affectedViewIds: ID[],
                                         recordId: ID): GraphicalTakeOffViewHistoryRecord => {
  return {
    recordId,
    affectedViewIds,
    activeToolName: details.activeToolName,
    currentTop: details.currentTop,
    drawingMeasurement: details.drawingMeasurement,
    drawingTop: details.drawingTop,
    highlightedDataBox: details.highlightedDataBox,
    highlightedMeasurement: details.highlightedMeasurement,
    highlightedTop: details.highlightedTop,
    measurementInProgress: details.measurementInProgress,
    selectedMeasurement: details.selectedMeasurement,
    selectedTop: details.selectedTop,
    view: graphicalTakeOffHousePlanViewArr.find(details.views, viewId)!,
    topViews: details.topViews[viewId],
  };
};

export const gtoRestoreDetails = (details: GraphicalTakeOffDetails,
                                  history: Hash<GraphicalTakeOffViewHistoryRecord>,
                                  shift: number): GraphicalTakeOffDetails =>
  keys(history).reduce((nextDetails, viewId) => gtoRestoreView(nextDetails, history[viewId], shift), details);

export const gtoRestoreView = (details: GraphicalTakeOffDetails,
                               history: GraphicalTakeOffViewHistoryRecord,
                               shift: number): GraphicalTakeOffDetails => {
  const viewId = getGraphicalTakeOffHousePlanViewId(history.view);

  return setEntityFields(details, {
    activeToolName: history.activeToolName,
    currentTop: history.currentTop,
    drawingMeasurement: history.drawingMeasurement,
    drawingTop: history.drawingTop,
    highlightedDataBox: history.highlightedDataBox,
    highlightedMeasurement: history.highlightedMeasurement,
    highlightedTop: history.highlightedTop,
    measurementInProgress: history.measurementInProgress,
    selectedMeasurement: history.selectedMeasurement,
    selectedTop: history.selectedTop,
    // for view restore only scale and dirty flag
    views: graphicalTakeOffHousePlanViewArr.update(
      details.views,
      viewId,
      (view) => setEntityFields(view, {
        picture: history.view.picture,
        isDirty: history.view.isDirty,
      })),
    topViews: setEntityField(details.topViews, viewId, history.topViews),
    history: shift ?
      setNestedEntityField(details.history, [viewId, 'currentIndex'], details.history[viewId].currentIndex + shift)
      : details.history,
  });
};

export const findGtoChangedViewIds = (oldDetails: GraphicalTakeOffDetails, details: GraphicalTakeOffDetails) => {
  const oldViewById = keyBy(oldDetails.views, getGraphicalTakeOffHousePlanViewId);
  const viewById = keyBy(details.views, getGraphicalTakeOffHousePlanViewId);

  const viewIds = intersection(
    oldDetails.views.map(getGraphicalTakeOffHousePlanViewId),
    details.views.map(getGraphicalTakeOffHousePlanViewId));
  const idsForChangedViews = viewIds.filter((viewId) => oldViewById[viewId] !== viewById[viewId]);
  const idsForChangedTopViews = viewIds.filter((viewId) => oldDetails.topViews[viewId] !== details.topViews[viewId]);
  const idsForCurrentView = oldDetails.currentTop !== details.currentTop ? [details.currentView] : [];

  return uniq(idsForChangedViews.concat(idsForChangedTopViews).concat(idsForCurrentView));
};

export const getGtoViewHistoryRecord = (details: GraphicalTakeOffDetails,
                                        viewId: ID,
                                        shift: number = 0): Maybe<GraphicalTakeOffViewHistoryRecord> => {
  const viewHistory = details.history[viewId];
  return viewHistory.records[viewHistory.currentIndex + shift];
};

export const getGtoAffectedViewsHavingAnotherCurrentRecord = (details: GraphicalTakeOffDetails) => {
  const currentRecord = getGtoViewHistoryRecord(details, details.currentView, 0);
  if (isNil(currentRecord)) {
    return [];
  }

  return currentRecord.affectedViewIds.filter((viewId) => {
    const affectedViewRecord = getGtoViewHistoryRecord(details, viewId, 0);
    return affectedViewRecord ? affectedViewRecord.recordId !== currentRecord.recordId : true;
  });
};

export const getGtoAffectedViewsHaingAnotherNextRecord = (details: GraphicalTakeOffDetails) => {
  const nextRecord = getGtoViewHistoryRecord(details, details.currentView, 1);
  if (isNil(nextRecord)) {
    return [];
  }

  return nextRecord.affectedViewIds.filter((viewId) => {
    const affectedViewRecord = getGtoViewHistoryRecord(details, viewId, 1);
    return affectedViewRecord ? affectedViewRecord.recordId !== nextRecord.recordId : true;
  });
};

export const getGtoPreviousViewHistoryRecords = (details: GraphicalTakeOffDetails): any => {
  const currentRecord = getGtoViewHistoryRecord(details, details.currentView, 0);
  if (isNil(currentRecord)) {
    return {};
  }

  return fromPairs(currentRecord.affectedViewIds.map((viewId) => [
    viewId,
    getGtoViewHistoryRecord(details, viewId, -1),
  ]));
};

export const getGtoNextViewHistoryRecords = (details: GraphicalTakeOffDetails): Hash<GraphicalTakeOffViewHistoryRecord> => {
  const nextRecord = getGtoViewHistoryRecord(details, details.currentView, 1);
  if (isNil(nextRecord)) {
    return {};
  }

  return fromPairs(nextRecord.affectedViewIds.map((viewId: any) => [
    viewId,
    getGtoViewHistoryRecord(details, viewId, 1),
  ]));
};

export const isGtoValidHorizontalScaleLine = (line: GraphicalTakeOffPoint) => {
  const angle = Math.abs(Math.acos(line.x / pointLength(line)));
  return angle < Math.PI / 4;
};

export const isGtoValidVerticalScaleLine = (line: GraphicalTakeOffPoint) => {
  const angle = Math.abs(Math.asin(line.y / pointLength(line)));
  return angle > Math.PI / 4 && angle < 3 * Math.PI / 4;
};

export const queryGtoScaleLinePxLength = ({ tools: { scaling: { point1, point2 } } }: GraphicalTakeOffDetails) =>
  point1 && point2 ? pointLength(diffPoints(point2!, point1!)) : 0;
